'use strict'

import cloneDeep from 'lodash/cloneDeep'
import {metalRequiresAssayOptIn} from '@/util/refinery/upstream/upstreamContract'
import {SETTLEMENT_DIALOG_MODES} from '@/util/refinery/upstream/constants'
import {isIsoDateTime} from '@/util/netsuiteLinks'
import {hydrateSettlementFormState, validateSettlementInput} from './settlementDraft'
import {requireActiveSalesOrder} from './netsuiteSalesOrder'

export const NETSUITE_SETTLEMENT_SCHEMA_VERSION = 1

const ENVELOPE_FIELDS = Object.freeze([
  'schemaVersion',
  'masterLotId',
  'masterLotCode',
  'salesOrder',
  'settlement',
])

const SETTLEMENT_INPUT_FIELDS = Object.freeze([
  'recordedAt',
  'partnerReferenceNumber',
  'reportedGrossToz',
  'perMetal',
  'contractFees',
  'assayOptIn',
  'otherFeeIncluded',
  'adHocFees',
  'notes',
  'documents',
  'dispositionMode',
  'dispositionDetails',
])

const PER_METAL_INPUT_FIELDS = Object.freeze(['purityPct', 'metalValueUsd'])

const DERIVED_SETTLEMENT_FIELDS = Object.freeze([
  'grossMetalCashUsd',
  'totalRefiningFeeUsd',
  'totalContractFeesUsd',
  'totalAdHocFeesUsd',
  'netCashUsd',
])

const DERIVED_PER_METAL_FIELDS = Object.freeze([
  'creditedFineToz',
  'refiningFeeUsd',
  'refiningFeeBasis',
  'refiningFeeMultiplier',
  'varianceVsManifestPct',
  'minimumReturnToz',
  'belowMinimumReturn',
  'excludedFromContract',
])

const DOCUMENT_TRANSPORT_FIELDS = Object.freeze(['source', 'sourceUrl', 'storageStatus'])

export const buildNetsuiteSettlementEnvelope = (masterLot) => {
  const input = settlementInputFromPersistedSettlement(masterLot)
  assertExplicitSettlementChoices(masterLot)
  return buildNetsuiteSettlementEnvelopeFromInput(masterLot, input)
}

export const settlementInputFromPersistedSettlement = (masterLot) => {
  assertPersistedSettlement(masterLot)
  const formState = hydratePersistedSettlement(masterLot)
  return {
    recordedAt: masterLot.settlement.recordedAt,
    partnerReferenceNumber: formState.partnerReferenceNumber,
    reportedGrossToz: formState.reportedGrossToz,
    perMetal: canonicalPerMetal(formState.perMetal),
    contractFees: cloneDeep(masterLot.settlement.contractFees || {}),
    assayOptIn: cloneDeep(formState.assayOptIn || {}),
    otherFeeIncluded: cloneDeep(formState.otherFeeIncluded || {}),
    adHocFees: cloneDeep(formState.adHocFees || []),
    notes: formState.notes || '',
    documents: canonicalDocuments(formState.documents),
    dispositionMode: formState.dispositionMode,
    dispositionDetails: cloneDeep(formState.dispositionDetails),
  }
}

export const buildNetsuiteSettlementEnvelopeFromInput = (masterLot, input) => {
  if (!masterLot?.id) throw new Error('master lot required')
  const envelope = normalizeNetsuiteSettlementEnvelope({
    schemaVersion: NETSUITE_SETTLEMENT_SCHEMA_VERSION,
    masterLotId: masterLot.id,
    masterLotCode: masterLot.masterLotCode,
    salesOrder: canonicalSalesOrder(masterLot),
    settlement: {
      recordedAt: input?.recordedAt,
      partnerReferenceNumber: input?.partnerReferenceNumber,
      reportedGrossToz: input?.reportedGrossToz,
      perMetal: canonicalPerMetal(input?.perMetal),
      contractFees: cloneDeep(input?.contractFees || {}),
      assayOptIn: cloneDeep(input?.assayOptIn || {}),
      otherFeeIncluded: cloneDeep(input?.otherFeeIncluded || {}),
      adHocFees: cloneDeep(input?.adHocFees || []),
      notes: input?.notes || '',
      documents: canonicalDocuments(input?.documents),
      dispositionMode: input?.dispositionMode,
      dispositionDetails: cloneDeep(input?.dispositionDetails),
    },
  })
  const errors = validateNetsuiteSettlementEnvelope(envelope, masterLot)
  if (errors.length) throw new Error(errors.join('; '))
  return envelope
}

export const normalizeNetsuiteSettlementEnvelope = (envelope) => {
  const normalized = cloneDeep(envelope || {})
  if (isObject(normalized.settlement)) {
    normalized.settlement.perMetal = lowerCaseObjectKeys(normalized.settlement.perMetal)
    normalized.settlement.assayOptIn = lowerCaseObjectKeys(normalized.settlement.assayOptIn)
    if (isObject(normalized.settlement.contractFees?.perMetalAssayFee)) {
      normalized.settlement.contractFees.perMetalAssayFee = lowerCaseObjectKeys(
        normalized.settlement.contractFees.perMetalAssayFee,
      )
    }
  }
  return sortObjectKeys(normalized)
}

export const validateNetsuiteSettlementEnvelope = (envelope, masterLot = null) => {
  if (!isObject(envelope)) return ['NetSuite settlement envelope required']
  const errors = [
    ...unknownFieldErrors(envelope, ENVELOPE_FIELDS, 'envelope'),
    ...validateEnvelopeIdentity(envelope, masterLot),
  ]
  if (!isObject(envelope.settlement)) {
    return [...errors, 'settlement payload required']
  }
  errors.push(
    ...unknownFieldErrors(envelope.settlement, SETTLEMENT_INPUT_FIELDS, 'settlement'),
    ...derivedFieldErrors(envelope.settlement, DERIVED_SETTLEMENT_FIELDS, 'settlement'),
    ...validatePerMetalFields(envelope.settlement.perMetal),
  )
  if (!isIsoDateTime(envelope.settlement.recordedAt)) {
    errors.push('settlement.recordedAt must be a valid ISO timestamp')
  }
  if (masterLot) {
    errors.push(...validateSettlementInput(
      masterLot,
      settlementInputFromNormalizedEnvelope(normalizeNetsuiteSettlementEnvelope(envelope)),
    ))
  }
  return errors
}

export const settlementInputFromNetsuiteEnvelope = (envelope) => {
  const errors = validateNetsuiteSettlementEnvelope(envelope)
  if (errors.length) throw new Error(errors.join('; '))
  return settlementInputFromNormalizedEnvelope(normalizeNetsuiteSettlementEnvelope(envelope))
}

export const netsuiteSettlementFingerprint = (envelope) =>
  JSON.stringify(normalizeNetsuiteSettlementEnvelope(envelope))

const hydratePersistedSettlement = (masterLot) => hydrateSettlementFormState({
  masterLot,
  mode: SETTLEMENT_DIALOG_MODES.EDIT,
  paidMetals: masterLot.contract?.paidMetals || [],
  contract: masterLot.contract || {},
  contractOtherFees: masterLot.contract?.otherFees || [],
  manifestGrossToz: masterLot.summary?.grossWeightToz,
})

const assertPersistedSettlement = (masterLot) => {
  if (!masterLot?.id) throw new Error('master lot required')
  if (!masterLot.settlement) throw new Error('persisted settlement required')
}

const assertExplicitSettlementChoices = (masterLot) => {
  const contract = masterLot.contract || {}
  const contractFees = masterLot.settlement.contractFees || {}
  const assayFees = contractFees.perMetalAssayFee || {}
  for (const metal of contract.paidMetals || []) {
    if (!metalRequiresAssayOptIn(contract, metal)) continue
    const row = assayFees[String(metal).toLowerCase()]
    if (typeof row?.optedIn !== 'boolean') {
      throw new Error(`explicit optedIn required for ${metal}`)
    }
  }
  for (const fee of contract.otherFees || []) {
    if (fee?.conditional !== true) continue
    const persisted = (contractFees.otherFees || []).find((row) => row?.code === fee.code)
    if (typeof persisted?.included !== 'boolean') {
      throw new Error(`explicit included required for ${fee.code}`)
    }
  }
}

const canonicalPerMetal = (perMetal) => Object.fromEntries(
  Object.entries(perMetal || {}).map(([metal, row]) => [
    String(metal).toLowerCase(),
    {
      purityPct: row?.purityPct,
      metalValueUsd: row?.metalValueUsd,
    },
  ]),
)

const canonicalDocuments = (documents) => (documents || []).map((document) => {
  const canonical = cloneDeep(document)
  for (const field of DOCUMENT_TRANSPORT_FIELDS) delete canonical[field]
  return canonical
})

const canonicalSalesOrder = (masterLot) => {
  const salesOrder = requireActiveSalesOrder(masterLot)
  return {
    netsuite_internalId: String(salesOrder.netsuite_internalId),
    tranId: salesOrder.tranId,
  }
}

const validateEnvelopeIdentity = (envelope, masterLot) => {
  const errors = []
  if (envelope.schemaVersion !== NETSUITE_SETTLEMENT_SCHEMA_VERSION) {
    errors.push(`unsupported schemaVersion: ${envelope.schemaVersion}`)
  }
  if (!nonEmptyString(envelope.masterLotId)) errors.push('masterLotId required')
  if (!nonEmptyString(envelope.masterLotCode)) errors.push('masterLotCode required')
  if (masterLot?.id && envelope.masterLotId !== masterLot.id) {
    errors.push('masterLotId does not match target lot')
  }
  if (masterLot?.masterLotCode && envelope.masterLotCode !== masterLot.masterLotCode) {
    errors.push('masterLotCode does not match target lot')
  }
  if (masterLot) {
    let expectedSalesOrder
    try {
      expectedSalesOrder = canonicalSalesOrder(masterLot)
    } catch (error) {
      errors.push(error.message)
    }
    if (expectedSalesOrder && (
      envelope.salesOrder?.netsuite_internalId !== expectedSalesOrder.netsuite_internalId ||
      envelope.salesOrder?.tranId !== expectedSalesOrder.tranId
    )) {
      errors.push('salesOrder identity does not match target lot')
    }
  }
  return errors
}

const validatePerMetalFields = (perMetal) => {
  if (!isObject(perMetal)) return ['settlement.perMetal must be an object']
  return Object.entries(perMetal).flatMap(([metal, row]) => {
    if (!isObject(row)) return [`settlement.perMetal.${metal} must be an object`]
    return [
      ...unknownFieldErrors(row, PER_METAL_INPUT_FIELDS, `settlement.perMetal.${metal}`),
      ...derivedFieldErrors(row, DERIVED_PER_METAL_FIELDS, `settlement.perMetal.${metal}`),
    ]
  })
}

const unknownFieldErrors = (value, allowedFields, path) =>
  Object.keys(value || {})
    .filter((field) => !allowedFields.includes(field))
    .map((field) => `unknown ${path} field: ${field}`)

const derivedFieldErrors = (value, derivedFields, path) =>
  derivedFields
    .filter((field) => Object.prototype.hasOwnProperty.call(value || {}, field))
    .map((field) => `${path}.${field} is PG-derived and not accepted`)

const settlementInputFromNormalizedEnvelope = (envelope) =>
  cloneDeep(Object.fromEntries(
    SETTLEMENT_INPUT_FIELDS.map((field) => [field, envelope.settlement?.[field]]),
  ))

const lowerCaseObjectKeys = (value) => {
  if (!isObject(value)) return value
  return Object.fromEntries(Object.entries(value).map(([key, row]) => [String(key).toLowerCase(), row]))
}

const sortObjectKeys = (value) => {
  if (Array.isArray(value)) return value.map(sortObjectKeys)
  if (!isObject(value)) return value
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, sortObjectKeys(value[key])]),
  )
}

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const nonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0
